import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { NextSeo } from "next-seo";
import { GetServerSideProps } from "next";
import { ApolloClient, InMemoryCache } from "@apollo/client";
import { handleError } from "@/helpers/handleError";
import { GET_CATEGORY_PAGINATION, GET_CATEGORY, GET_HEADER_DATA, GET_FOOTER_DATA, GET_HOMEPAGE_DATA } from "@/graphql/queries";
import { BreadcrumbType } from "@/Types/types";
import { breadcrumbInitial } from "@/data/breadcrumbInitial";

import HeaderMenu from "@/components/HeaderMenu";
import Footer from "@/components/Footer";
import RecentPosts from "@/components/MainPage/RecentPosts";
import Breadcrumbs from "@/components/Breadcrumbs";
import TrendingCarousel from "@/components/MainPage/TrendingCarousel";

export default function CategoryPage(props: any) {
  const [counter, setCounter] = useState<number>();
  const [isMobile, setIsMobile] = useState<Boolean>();

  useEffect(() => {
    setIsMobile(window.innerWidth <= 991 ? true : false);
  })

  useEffect(() => {
    setCounter(props.categoryPosts.meta.pagination.total);
  }, [props]);

  return (
    <Box bgColor="#FFFCF6">
      <HeaderMenu cookies={props.cookies.data} menu={props.header[0].items} submenu={props.navbar.data?.attributes.submenu_header} />
      <NextSeo
        title={props.category.data[0].attributes.metaTitle}
        description={props.category.data[0].attributes.metaDescription}
        canonical={props.category.data[0].attributes.canonicalURL}
      />
      <Box
        maxWidth="1216px"
        margin="0 auto"
        width="90%"
        mb="32px"
        as="header"
        pt={{ base: "0px", lg: "80px" }}
      >
        <Box mb="32px" >
          <Breadcrumbs data={props.breadcrumbs} />
        </Box>
        <Flex alignItems="center" gap="24px" mb="32px" flexWrap="wrap">
          <Heading 
            as="h1" 
            fontSize={{ base: "30px", lg: "36px" }}
            lineHeight={{ base: "36px", lg: "40px" }}
            fontWeight="800" 
          >
            {props.category.data[0]?.attributes.title}
          </Heading>
          <Box
            as="span"
            bgColor="purple.50"
            padding="10px"
            borderRadius="4px"
          >
            <Text
              fontSize="16px"
              lineHeight="24px"
              fontWeight="400"
              color="purple.1000"
            >
              {counter} publicações
            </Text>
          </Box>
        </Flex>
        <Text
          fontSize="16px"
          lineHeight="24px"
          color="gray.600"
        >
          {props.category.data[0]?.attributes.description}
        </Text>
      </Box>
      <RecentPosts data={props.categoryPosts.data} />
      {!isMobile && Boolean(props.trending.data.length) && (
        <TrendingCarousel
          data={props.trending.data}
        />
      )}
      <Footer menu={props.footer[0].items} />
    </Box>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    context.res.setHeader(
      "Cache-Control",
      "public, s-maxage=10, stale-while-revalidate=59"
    );

    const client = new ApolloClient({
      uri: `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
      cache: new InMemoryCache(),
    });

    const { data: headerData } = await client.query({
      query: GET_HEADER_DATA,
    });

    const { data: footerData } = await client.query({
      query: GET_FOOTER_DATA,
    });

    const { data: categoryData } = await client.query({
      query: GET_CATEGORY,
      variables: {
        slugUrl: context.params?.slug,
      },
    });

    if (!categoryData.category.data.length) {
      return {
        notFound: true,
      };
    }

    const { data: categoryPosts } = await client.query({
      query: GET_CATEGORY_PAGINATION,
      variables: {
        slugUrl: context.params?.slug,
        page: (context.query?.page && +context.query?.page) || 1,
      },
    });

    const { data: homepageData } = await client.query({
      query: GET_HOMEPAGE_DATA,
    });

    const breadcrumbs: BreadcrumbType[] = JSON.parse(JSON.stringify(breadcrumbInitial));

    breadcrumbs.push({
      href: `/`,
      label: `Categorias`,
    });
    breadcrumbs.push({
      href: `/blog/categoria/${categoryData.category.data[0].attributes.slug}`,
      label: `${categoryData.category.data[0].attributes.title}`,
    });

    return {
      props: {
        ...categoryPosts,
        ...categoryData,
        ...homepageData,
        ...headerData,
        ...footerData,
        breadcrumbs
      },
    };
  } catch (error) {
    handleError(error, "Categoria Slug getServerSideProps");
    return {
      notFound: true,
    };
  }
};
